import React, { useState } from 'react';
import { api } from '../../services/apiClient';
import { theme } from '../../styles/theme';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { useAuth } from '../../context/AuthContext';

const DashboardProfile = ({ showSuccess, showError, showConfirm }) => {
  const { userProfile, isApproved, fetchUserProfile } = useAuth();

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  const handleEdit = () => {
    setEditing(true);
    setFormData({
      email: userProfile?.email || '',
      phone: userProfile?.phone || '',
      state: userProfile?.state || '',
      company: userProfile?.company || '',
      business_name: userProfile?.business_name || '',
      tax_id: userProfile?.tax_id || '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    await showConfirm({
      title: 'Confirm Profile Changes',
      message: 'Are you sure you want to update your profile information?',
      type: 'info',
      confirmText: 'Save Changes',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          setLoading(true);
          const updateData = {};
          Object.keys(formData).forEach(key => {
            if (formData[key] && formData[key].trim() !== '') {
              updateData[key] = formData[key].trim();
            }
          });
          
          await api.users.updateProfile(updateData);
          await fetchUserProfile();
          showSuccess('Profile updated successfully!', 'Profile Updated');
          setEditing(false);
        } catch (err) {
          const errorDetail = err.response?.data?.detail;
          let errorMsg = 'Failed to update profile';
          if (Array.isArray(errorDetail)) {
            errorMsg += ': ' + errorDetail.map(e => `${e.loc?.join('.') || 'Field'}: ${e.msg}`).join(', ');
          } else if (typeof errorDetail === 'string') {
            errorMsg += ': ' + errorDetail;
          } else {
            errorMsg += ': ' + err.message;
          }
          showError(errorMsg);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const styles = {
    container: {
      width: '100%',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.xl,
      gap: theme.spacing.md,
      flexWrap: 'wrap',
    },
    title: {
      fontSize: theme.typography.fontSize['3xl'],
      fontFamily: theme.typography.fontFamily.primary,
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      margin: 0,
    },
    formContainer: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    formSubtitle: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textLight,
      margin: `${theme.spacing.xs} 0 0 0`,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    formSection: {
      padding: theme.spacing.xl,
      borderBottom: `1px solid ${theme.colors.borderLight}`,
    },
    formSectionLast: {
      padding: theme.spacing.xl,
    },
    sectionHeader: {
      marginBottom: theme.spacing.lg,
    },
    sectionTitle: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      margin: 0,
      marginBottom: theme.spacing.xs,
      fontFamily: theme.typography.fontFamily.secondary,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    sectionDescription: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textLight,
      margin: 0,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: theme.spacing.lg,
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.sm,
    },
    formGroupFull: {
      gridColumn: '1 / -1',
    },
    label: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      fontFamily: theme.typography.fontFamily.secondary,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.xs,
    },
    labelHint: {
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.normal,
      color: theme.colors.textLight,
      fontStyle: 'italic',
    },
    input: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSize.base,
      fontFamily: theme.typography.fontFamily.secondary,
      border: `2px solid ${theme.colors.borderLight}`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
      transition: `all ${theme.transitions.base}`,
      outline: 'none',
    },
    inputReadonly: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSize.base,
      fontFamily: theme.typography.fontFamily.secondary,
      border: `2px solid ${theme.colors.borderLight}`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: '#F3F4F6',
      color: theme.colors.textLight,
      cursor: 'not-allowed',
    },
    formActionsModern: {
      display: 'flex',
      gap: theme.spacing.md,
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      padding: theme.spacing.xl,
      backgroundColor: '#F9F9F9',
      borderTop: `2px solid ${theme.colors.borderLight}`,
    },
    warningBanner: {
      backgroundColor: '#FFF3CD',
      border: `2px solid #F59E0B`,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      marginBottom: theme.spacing.xl,
    },
    warningTitle: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.bold,
      color: '#92400E',
      margin: `0 0 ${theme.spacing.sm} 0`,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    warningText: {
      fontSize: theme.typography.fontSize.base,
      color: '#92400E',
      margin: 0,
      fontFamily: theme.typography.fontFamily.secondary,
    },
    viewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: theme.spacing.xl,
    },
    viewField: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.sm,
    },
    viewLabel: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.textLight,
      fontFamily: theme.typography.fontFamily.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    viewValue: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.text,
      fontFamily: theme.typography.fontFamily.secondary,
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
    },
    memberBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
      backgroundColor: '#FFF8F0',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.full,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.primary,
    },
  };

  const responsiveStyles = `
    .profile-form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: ${theme.spacing.lg};
    }

    .profile-form-full {
      grid-column: 1 / -1;
    }

    .profile-input:focus {
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 4px rgba(204, 85, 0, 0.1);
      transform: translateY(-1px);
    }

    .profile-view-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: ${theme.spacing.xl};
    }

    @media (max-width: 768px) {
      .profile-form-grid,
      .profile-view-grid {
        grid-template-columns: 1fr;
        gap: ${theme.spacing.md};
      }

      .profile-header {
        flex-direction: column;
        align-items: flex-start;
        gap: ${theme.spacing.md};
      }

      .profile-header h2 {
        font-size: ${theme.typography.fontSize['2xl']};
      }

      .form-section,
      .form-section-last,
      .form-actions-modern {
        padding: ${theme.spacing.lg};
      }
    }

    @media (max-width: 480px) {
      .profile-form-actions {
        flex-direction: column-reverse;
      }

      .profile-form-actions button {
        width: 100%;
      }

      .form-section,
      .form-section-last {
        padding: ${theme.spacing.md};
      }

      .form-actions-modern {
        padding: ${theme.spacing.md};
      }
    }
  `;

  if (editing) {
    return (
      <>
        <style>{responsiveStyles}</style>
        <div style={styles.container}>
          <div style={styles.header} className="profile-header">
            <div>
              <h2 style={styles.title}>Edit Profile</h2>
              <p style={styles.formSubtitle}>Update your account information</p>
            </div>
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
          </div>

          {!isApproved && (
            <div style={styles.warningBanner}>
              <h3 style={styles.warningTitle}>Account Pending Approval</h3>
              <p style={styles.warningText}>Your account is currently pending approval. Once approved, you'll gain full access to all features.</p>
            </div>
          )}

          <div style={styles.formContainer}>
            <Card variant="parchment" padding="none">
              <form onSubmit={handleSubmit}>
                {/* Personal Information Section */}
                <div style={styles.formSection}>
                  <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Personal Information</h3>
                    <p style={styles.sectionDescription}>Your basic account details (name cannot be changed)</p>
                  </div>
                  <div className="profile-form-grid">
                    <div className="form-group">
                      <label style={styles.label}>
                        First Name
                        <span style={styles.labelHint}>Read-only</span>
                      </label>
                      <div style={styles.inputReadonly}>{userProfile?.first_name || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label style={styles.label}>
                        Last Name
                        <span style={styles.labelHint}>Read-only</span>
                      </label>
                      <div style={styles.inputReadonly}>{userProfile?.last_name || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div style={styles.formSection}>
                  <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Contact Information</h3>
                    <p style={styles.sectionDescription}>How we can reach you</p>
                  </div>
                  <div className="profile-form-grid">
                    <div className="form-group">
                      <label style={styles.label}>
                        Email Address *
                        <span style={styles.labelHint}>Used for account notifications</span>
                      </label>
                      <input 
                        type="email" 
                        value={formData.email} 
                        onChange={(e) => setFormData({...formData, email: e.target.value})} 
                        required 
                        style={styles.input}
                        className="profile-input"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div className="form-group">
                      <label style={styles.label}>
                        Phone Number *
                        <span style={styles.labelHint}>For order updates and support</span>
                      </label>
                      <input 
                        type="tel" 
                        value={formData.phone} 
                        onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                        required 
                        style={styles.input}
                        className="profile-input"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div className="form-group">
                      <label style={styles.label}>
                        State *
                        <span style={styles.labelHint}>Your primary state of residence</span>
                      </label>
                      <input 
                        type="text" 
                        value={formData.state} 
                        onChange={(e) => setFormData({...formData, state: e.target.value})} 
                        required 
                        style={styles.input}
                        className="profile-input"
                        placeholder="CA"
                      />
                    </div>
                  </div>
                </div>

                {/* Business Information Section */}
                <div style={styles.formSectionLast}>
                  <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Business Information</h3>
                    <p style={styles.sectionDescription}>Optional business details for wholesale accounts</p>
                  </div>
                  <div className="profile-form-grid">
                    <div className="form-group">
                      <label style={styles.label}>
                        Company Name
                        <span style={styles.labelHint}>Optional</span>
                      </label>
                      <input 
                        type="text" 
                        value={formData.company} 
                        onChange={(e) => setFormData({...formData, company: e.target.value})} 
                        style={styles.input}
                        className="profile-input"
                        placeholder="Your Company LLC"
                      />
                    </div>

                    <div className="form-group">
                      <label style={styles.label}>
                        Business Name
                        <span style={styles.labelHint}>Optional - DBA or trade name</span>
                      </label>
                      <input 
                        type="text" 
                        value={formData.business_name} 
                        onChange={(e) => setFormData({...formData, business_name: e.target.value})} 
                        style={styles.input}
                        className="profile-input"
                        placeholder="Your Business Name"
                      />
                    </div>

                    <div className="form-group profile-form-full">
                      <label style={styles.label}>
                        Tax Permit #
                        <span style={styles.labelHint}>Optional - Seller's permit or sales tax account number</span>
                      </label>
                      <input 
                        type="text" 
                        value={formData.tax_id} 
                        onChange={(e) => setFormData({...formData, tax_id: e.target.value})} 
                        style={styles.input}
                        className="profile-input"
                        placeholder="XX-XXXXXXX"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div style={styles.formActionsModern} className="profile-form-actions">
                  <Button variant="outline" onClick={() => setEditing(false)} type="button" size="lg">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} size="lg">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={styles.container}>
        <div style={styles.header} className="profile-header">
          <h2 style={styles.title}>Account Information</h2>
          <Button onClick={handleEdit}>Edit Profile</Button>
        </div>

        {!isApproved && (
          <div style={styles.warningBanner}>
            <h3 style={styles.warningTitle}>Account Pending Approval</h3>
            <p style={styles.warningText}>Your account is currently pending approval. Once approved, you'll gain full access to all features.</p>
          </div>
        )}

        <Card variant="parchment" padding="xl">
          <div className="profile-view-grid" style={styles.viewGrid}>
            <div style={styles.viewField}>
              <div style={styles.viewLabel}>First Name</div>
              <div style={styles.viewValue}>{userProfile?.first_name || 'N/A'}</div>
            </div>

            <div style={styles.viewField}>
              <div style={styles.viewLabel}>Last Name</div>
              <div style={styles.viewValue}>{userProfile?.last_name || 'N/A'}</div>
            </div>

            <div style={styles.viewField}>
              <div style={styles.viewLabel}>Email Address</div>
              <div style={styles.viewValue}>{userProfile?.email || 'N/A'}</div>
            </div>

            <div style={styles.viewField}>
              <div style={styles.viewLabel}>Phone Number</div>
              <div style={styles.viewValue}>{userProfile?.phone || 'N/A'}</div>
            </div>

            <div style={styles.viewField}>
              <div style={styles.viewLabel}>State</div>
              <div style={styles.viewValue}>{userProfile?.state || 'N/A'}</div>
            </div>

            <div style={styles.viewField}>
              <div style={styles.viewLabel}>Company</div>
              <div style={styles.viewValue}>{userProfile?.company || 'N/A'}</div>
            </div>

            <div style={styles.viewField}>
              <div style={styles.viewLabel}>Business Name</div>
              <div style={styles.viewValue}>{userProfile?.business_name || 'N/A'}</div>
            </div>

            <div style={styles.viewField}>
              <div style={styles.viewLabel}>Tax Permit #</div>
              <div style={styles.viewValue}>{userProfile?.tax_id ? '••••••••' : 'N/A'}</div>
            </div>

            <div style={{...styles.viewField, ...styles.formGroupFull}}>
              <div style={styles.viewLabel}>Member Since</div>
              <div style={styles.memberBadge}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM8 14C4.7 14 2 11.3 2 8C2 4.7 4.7 2 8 2C11.3 2 14 4.7 14 8C14 11.3 11.3 14 8 14Z" fill="currentColor"/>
                  <path d="M8 4C7.4 4 7 4.4 7 5V8.4L10.3 10.3C10.8 10.6 11.4 10.4 11.7 9.9C12 9.4 11.8 8.8 11.3 8.5L9 7.2V5C9 4.4 8.6 4 8 4Z" fill="currentColor"/>
                </svg>
                {userProfile?.created_at ? new Date(userProfile.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                }) : 'N/A'}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
};

export default DashboardProfile;
